import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { acceptedMotion, type MotionAttachment, motionMayPlay, motionPosterLoaded, synchronizeMotion } from "./background-motion-controller";
import { stageFetch, trackStageMetric, type EpisodeManifest } from "./stage-api";
import { loadStageCharacterBindings } from "./stage-character-package";

type Connection = EventTarget & { saveData?: boolean };
export function BackgroundMotion({ episode, objectPosition, motionEnabled }: {
  episode: EpisodeManifest; objectPosition: string; motionEnabled: boolean;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const posterImage = useRef<HTMLImageElement>(null);
  const [attachment, setAttachment] = useState<MotionAttachment | null>(null);
  const url = attachment?.url ?? null;
  const poster = attachment?.posterUrl ?? null;
  const [posterReady, setPosterReady] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const visibility = useRef(true);

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: Connection }).connection;
    // The shared viewer choice already resolves the system default and explicit opt-in.
    const update = () => setAllowed(motionMayPlay(document.hidden, visibility.current, !motionEnabled,
      Boolean(connection?.saveData), false));
    const node = video.current;
    const observer = new IntersectionObserver(([entry]) => { visibility.current = entry.isIntersecting; update(); });
    if (node) observer.observe(node);
    connection?.addEventListener("change", update);
    document.addEventListener("visibilitychange", update);
    update();
    return () => {
      observer.disconnect();
      connection?.removeEventListener("change", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, [motionEnabled]);

  useEffect(() => {
    const abort = new AbortController();
    setAttachment(null); setReady(false);
    const bindingsPromise = loadStageCharacterBindings(abort.signal);
    let requestNumber = 0;
    const refresh = async () => {
      const requestId = ++requestNumber;
      if (document.hidden) return;
      try {
        const bindings = await bindingsPromise;
        const hashes = Object.fromEntries(Object.entries(bindings).map(([key, b]) => [key, b.manifestSha256]));
        const data = await stageFetch<{ backgroundMotion: unknown }>(`/api/episodes/${encodeURIComponent(episode.id)}/background-motion`,
          { signal: abort.signal, cache: "no-store" }, 4500);
        const next = acceptedMotion(data.backgroundMotion, episode.id, episode.assetHashes?.background, hashes);
        if (!abort.signal.aborted && requestId === requestNumber) setAttachment(next);
      } catch { if (!abort.signal.aborted && requestId === requestNumber) setAttachment(null); }
    };
    void refresh();
    // A no-store control check withdraws cached motion within five seconds of an operator disable.
    const timer = window.setInterval(() => void refresh(), 5000);
    return () => {
      abort.abort(); window.clearInterval(timer);
    };
  }, [episode.id, episode.assetHashes?.background]);

  useLayoutEffect(() => {
    setFailed(false); setReady(false);
    // Reset before browser load/playing events, and recover an already-cached exact poster.
    setPosterReady(motionPosterLoaded(posterImage.current, poster ? new URL(poster, document.baseURI).href : null));
  }, [url, poster]);
  useEffect(() => {
    const node = video.current;
    if (!node) return;
    let disposed = false;
    void synchronizeMotion(node, allowed && Boolean(url) && !failed, () => !disposed).then(ok => {
      if (disposed) return;
      if (!disposed && allowed && url && !failed && !ok) {
        setFailed(true); void trackStageMetric("background_motion_fallback", episode.id);
      }
    });
    return () => { disposed = true; node.pause(); };
  }, [allowed, url, failed, episode.id]);

  return <><img ref={posterImage} className={`stage-backdrop stage-motion-poster ${posterReady && poster ? "is-ready" : ""}`}
    src={poster ?? undefined} alt="" aria-hidden="true" style={{ objectPosition }}
    onLoad={() => setPosterReady(true)} onError={() => { setPosterReady(false); setFailed(true); }} />
    <video ref={video} className={`stage-backdrop stage-motion ${ready && posterReady && Boolean(url) && allowed && !failed ? "is-ready" : ""}`}
    src={!failed ? url ?? undefined : undefined} muted loop playsInline preload="none" aria-hidden="true"
    data-motion-enabled={motionEnabled}
    style={{ objectPosition }} onPlaying={() => setReady(true)} onError={() => {
      setFailed(true); setReady(false); void trackStageMetric("background_motion_fallback", episode.id);
    }} /></>;
}
